import { BookedSkatingPass } from '@types';
import { PDFViewerModal } from '_components/atoms';
import UserService from '_services/UserService';
import HelperMethods from '_utils/HelperMethods';
import React, { useEffect, useState } from 'react';
import Barcode from 'react-barcode';

const MyBookedSkatingPasses: React.FC = () => {
  const [passes, setPasses] = useState<BookedSkatingPass[]>([]);
  const [loading, setLoading] = useState(true);
  const [pdfViewerModal, setPdfViewerModal] = useState<{
    isOpen: boolean;
    url: string;
    title: string;
  }>({
    isOpen: false,
    url: '',
    title: '',
  });

  const fetchPasses = async () => {
    try {
      setLoading(true);

      const responseData =
        (await UserService.getBookedSkatingPasses()) as unknown as BookedSkatingPass[];

      if (responseData) {
        setPasses(responseData);
      }
    } catch (error) {
      console.error('Error fetching passes:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPasses();
  }, []);

  const openPdfViewer = (url: string, title: string) => {
    setPdfViewerModal({
      isOpen: true,
      url,
      title,
    });
  };

  const closePdfViewer = () => {
    setPdfViewerModal({
      isOpen: false,
      url: '',
      title: '',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            My Booked Skating Passes
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            View your booked skating passes
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <div className="text-sm text-gray-600">
            Total Passes: <span className="font-semibold">{passes.length}</span>
          </div>
        </div>
      </div>

      {/* Passes Table - Desktop */}
      <div className="hidden md:block bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"></th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Pass Info
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Usage
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider text-center">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {passes.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    {loading ? 'Loading...' : 'No passes found'}
                  </td>
                </tr>
              ) : (
                passes.map(pass => (
                  <tr key={pass.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col items-center">
                        <div className="mb-2">
                          <Barcode
                            value={pass.barcodeId}
                            width={1}
                            height={50}
                            fontSize={10}
                            format="CODE128"
                          />
                        </div>
                        <div className="text-xs text-gray-500 text-center">
                          {pass.barcodeId}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {pass.passName}
                      </div>
                      <div className="text-sm text-gray-500">
                        Pass #{pass.skatingPassId}
                      </div>
                      <div className="text-xs text-gray-400">
                        Receipt: {pass.receiptNumber}
                      </div>
                      <div className="text-xs text-gray-400">
                        Booked:{' '}
                        {HelperMethods.formatDateTimeLocal(pass.createdAt)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {pass.customerName}
                      </div>
                      <div className="text-sm text-gray-500">
                        {pass.customerEmail}
                      </div>
                      <div className="text-sm text-gray-500">
                        {pass.customerPhone}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {(() => {
                          const maxUses =
                            HelperMethods.getEffectiveMaxUses(pass);
                          return maxUses == null
                            ? `${pass.redeemedCount || 0} / Unlimited`
                            : `${pass.redeemedCount || 0} / ${maxUses}`;
                        })()}
                      </div>
                      <div className="text-sm text-gray-500">
                        {HelperMethods.getEffectiveMaxUses(pass) == null
                          ? 'unlimited passes'
                          : '# passes used'}
                      </div>
                      {HelperMethods.getEffectiveMaxUses(pass) != null && (
                        <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                          <div
                            className="bg-blue-600 h-2 rounded-full"
                            style={{
                              width: `${Math.min(
                                100,
                                ((pass.redeemedCount || 0) /
                                  (HelperMethods.getEffectiveMaxUses(pass) ||
                                    1)) *
                                  100,
                              )}%`,
                            }}
                          ></div>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2 justify-between">
                        {pass.waiverUrl ? (
                          <button
                            onClick={() =>
                              openPdfViewer(pass.waiverUrl!, 'View Waiver')
                            }
                            className="text-blue-600 hover:text-blue-900"
                          >
                            View Waiver
                          </button>
                        ) : (
                          <div className="text-gray-500">No Waiver</div>
                        )}
                        {pass.receiptUrl && (
                          <button
                            onClick={() =>
                              openPdfViewer(pass.receiptUrl!, 'View Receipt')
                            }
                            className="text-blue-600 hover:text-blue-900"
                          >
                            View Receipt
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Passes Cards - Mobile */}
      <div className="md:hidden space-y-4">
        {passes.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center text-gray-500">
            {loading ? 'Loading...' : 'No passes found'}
          </div>
        ) : (
          passes.map(pass => (
            <div
              key={pass.id}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
            >
              {/* Pass Info */}
              <div className="mb-4">
                <div className="mb-2">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {pass.passName}
                  </h3>
                </div>
                <div className="text-sm text-gray-600">
                  Pass #{pass.skatingPassId}
                </div>
                <div className="text-xs text-gray-400 mt-1">
                  Receipt: {pass.receiptNumber}
                </div>
                <div className="text-xs text-gray-400">
                  Booked: {HelperMethods.formatDateTimeLocal(pass.createdAt)}
                </div>
              </div>

              {/* Usage Info */}
              <div className="mb-4 p-3 bg-gray-50 rounded-md">
                <h4 className="text-sm font-medium text-gray-900 mb-2">
                  Usage
                </h4>
                <div className="text-sm text-gray-600">
                  <div className="font-medium">
                    {(() => {
                      const maxUses = HelperMethods.getEffectiveMaxUses(pass);
                      return maxUses == null
                        ? `${pass.redeemedCount || 0} / Unlimited uses`
                        : `${pass.redeemedCount || 0} / ${maxUses} uses`;
                    })()}
                  </div>
                  {HelperMethods.getEffectiveMaxUses(pass) != null && (
                    <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full"
                        style={{
                          width: `${Math.min(
                            100,
                            ((pass.redeemedCount || 0) /
                              (HelperMethods.getEffectiveMaxUses(pass) || 1)) *
                              100,
                          )}%`,
                        }}
                      ></div>
                    </div>
                  )}
                </div>
              </div>

              {/* Customer Info */}
              <div className="mb-4 p-3 bg-gray-50 rounded-md">
                <h4 className="text-sm font-medium text-gray-900 mb-2">
                  Customer
                </h4>
                <div className="text-sm text-gray-600">
                  <div className="font-medium">{pass.customerName}</div>
                  <div>{pass.customerEmail}</div>
                  <div>{pass.customerPhone}</div>
                </div>
              </div>

              {/* Barcode Info */}
              <div className="mb-4 p-3 bg-gray-50 rounded-md">
                <div className="flex flex-col items-center">
                  <div className="mb-2">
                    <Barcode
                      value={pass.barcodeId}
                      width={1}
                      height={60}
                      fontSize={12}
                      format="CODE128"
                    />
                  </div>
                  <div className="text-xs text-gray-500 text-center">
                    {pass.barcodeId}
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2">
                {pass.waiverUrl && (
                  <button
                    onClick={() =>
                      openPdfViewer(pass.waiverUrl!, 'View Waiver')
                    }
                    className="inline-flex items-center px-3 py-2 bg-blue-100 text-blue-700 text-sm rounded-md hover:bg-blue-200 transition-colors"
                  >
                    View Waiver
                  </button>
                )}
                {pass.receiptUrl && (
                  <button
                    onClick={() =>
                      openPdfViewer(pass.receiptUrl!, 'View Receipt')
                    }
                    className="inline-flex items-center px-3 py-2 bg-blue-100 text-blue-700 text-sm rounded-md hover:bg-blue-200 transition-colors"
                  >
                    View Receipt
                  </button>
                )}
              </div>

              {/* Last Used Info */}
              {pass.redeemedBy && (
                <div className="mt-3 text-xs text-gray-500">
                  Last used by: {pass.redeemedBy}
                </div>
              )}
              {pass.redeemedAt && (
                <div className="mt-1 text-xs text-gray-500">
                  Last used:{' '}
                  {HelperMethods.formatDateTimeLocal(pass.redeemedAt)}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* PDF Viewer Modal */}
      <PDFViewerModal
        isOpen={pdfViewerModal.isOpen}
        onClose={closePdfViewer}
        url={pdfViewerModal.url}
        title={pdfViewerModal.title}
      />
    </div>
  );
};

export default MyBookedSkatingPasses;
